import React, { useState } from 'react';
import Navigation from '../components/Navigation';
import TourGuide from '../components/TourGuide';
import { submitTableCaptainRequest, checkIfTableCaptainApprovalExists, inviteGuest, getMyGuests, requestAdditionalTable, getCaptainData, updateGuestTables, revokeGuest, remindGuest } from '../api';
import { useSelector } from 'react-redux';

function Guests() {
    const userInfo = useSelector((state) => state.user.userInfo);
    const isAuthLoading = useSelector((state) => state.user.isAuthLoading);
    const isTableCaptain = userInfo?.userRoles?.includes('table_captain');

    // Deadline Countdown State
    const [timeLeft, setTimeLeft] = useState('');
    const [isPastDeadline, setIsPastDeadline] = useState(false);

    React.useEffect(() => {
        const calculateTimeLeft = () => {
            // April 8, 2026 00:00:00 PDT (UTC-7)
            const deadline = new Date("2026-04-18T00:00:00-07:00");
            const now = new Date();
            const difference = deadline - now;

            if (difference > 0) {
                const days = Math.floor(difference / (1000 * 60 * 60 * 24));
                const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
                const minutes = Math.floor((difference / 1000 / 60) % 60);
                const seconds = Math.floor((difference / 1000) % 60);
                setTimeLeft(`${days}d ${hours}h ${minutes}m ${seconds}s`);
                setIsPastDeadline(false);
            } else {
                setTimeLeft("Deadline Passed");
                setIsPastDeadline(true);
            }
        };

        calculateTimeLeft();
        const timer = setInterval(calculateTimeLeft, 1000);

        return () => clearInterval(timer);
    }, []);

    // Determine if data is loading
    // const isLoading = userInfo && !captainData;

    // Guests state
    const [guests, setGuests] = useState([]);
    const [isGuestsLoading, setIsGuestsLoading] = useState(false);
    const [captainData, setCaptainData] = useState(null);

    const fetchGuests = React.useCallback(async () => {
        if (userInfo?.userId) {
            setIsGuestsLoading(true);
            try {
                const [guestData, capData] = await Promise.all([
                    getMyGuests(userInfo.userId),
                    getCaptainData(userInfo.userId)
                ]);
                // Normalize guest data to ensure consistent property usage
                const normalizedGuests = guestData.map(g => ({
                    ...g,
                    tableNumber: g.tableNumber || g.table_number || 1
                }));
                setGuests(normalizedGuests);
                setOriginalGuests(JSON.parse(JSON.stringify(normalizedGuests)));
                setCaptainData(Array.isArray(capData) ? capData[0] : capData);
            } catch (error) {
                console.error("Failed to fetch data", error);
            } finally {
                setIsGuestsLoading(false);
            }
        }
    }, [userInfo]);

    // Notification state lifted up
    const [notifications, setNotifications] = useState([]);
    const addNotification = (guestName, type = 'success', message = '', context = 'invite') => {
        const id = Date.now() + Math.random();
        setNotifications(prev => [...prev, { id, guestName, type, message, context }]);
    };
    const removeNotification = (id) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    };

    React.useEffect(() => {
        if (isTableCaptain) {
            fetchGuests();
        }
    }, [isTableCaptain, fetchGuests]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
    const [isRequestingTable, setIsRequestingTable] = useState(false);
    const [approvalExists, setApprovalExists] = useState(false);
    const [approvalDataLoaded, setApprovalDataLoaded] = useState(false);
    const [originalGuests, setOriginalGuests] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedGuestForMove, setSelectedGuestForMove] = useState(null);

    // Tutorial State
    const [showTutorial, setShowTutorial] = useState(false);
    const tourSteps = React.useMemo(() => {
        const hasDeclined = guests.some(g => g.status === 'Declined');
        const declinedStep = hasDeclined ? {
            target: '#declined-invitations-section',
            title: 'Declined Invitations',
            content: 'If an invited guest declines, they will appear here. Their invitation is no longer valid, and their seat becomes free for you to invite someone else.'
        } : {
            target: '#table-visualization-area',
            placement: 'bottom', // Hint for positioning if possible, though currently JS calculates it
            title: 'Declined Invitations',
            content: 'If any guests decline their invitation in the future, they will appear in a section below this area. You can then choose to re-invite them if needed.'
        };

        return [
            {
                target: '#my-table-header',
                title: 'Welcome Table Captain!',
                content: 'This is your dashboard where you can invite guests and manage your table assignments.'
            },
            {
                target: '#deadline-timer',
                title: 'Watch the Deadline',
                content: 'Keep an eye on this timer. All guest details must be finalized by April 8th.'
            },
            {
                target: '#add-guest-form',
                title: 'Invite & Manage Guests',
                content: 'Use the form on the left to invite new guests by entering their name and email. On the right, you can see details for guests you have already invited. Note: If a guest declines, their invitation becomes invalid. You will need to re-invite them if they wish to attend.'
            },
            {
                target: '#save-changes-btn',
                title: 'Save & Send Invitations',
                content: 'Click "Save Changes" to send email invitations to new guests and save your table arrangements. Always remember to save after rearranging your guests!'
            },
            {
                target: '#table-visualization-area',
                title: 'Arrange Your Tables',
                content: 'Visualize and arrange your tables here! On a desktop, you can drag and drop guests. On mobile, or for a faster method, simply tap a guest to select them, then tap their new destination table to move them there.\n\nYou can temporarily have more than 10 guests at a table while rearranging, but you cannot save until each table has 10 or fewer guests. \n\nNeed more space? Once you reach 10 invited guests, an option will appear to request an additional table from the admins.'
            },
            declinedStep,
            {
                target: '#refresh-data-btn',
                title: 'Stay Up to Date',
                content: 'Click here to fetch the latest RSVP statuses accepted by your guests.'
            }
        ];
    }, [guests]);

    const tableCount = captainData?.tables || 1;
    const maxGuests = tableCount * 10;

    React.useEffect(() => {
        if (isAuthLoading) return;

        if (userInfo && !isTableCaptain) {
            setApprovalDataLoaded(false);
            checkIfTableCaptainApprovalExists(userInfo.userId).then((exists) => {
                setApprovalExists(exists);
            }).finally(() => {
                setApprovalDataLoaded(true);
            });
        } else {
            // If user is not logged in or is already a captain, we don't need to check
            setApprovalDataLoaded(true);
        }
    }, [userInfo, isTableCaptain, isAuthLoading]);

    // Application Form State
    const [applicationForm, setApplicationForm] = useState({
        attendedLastYear: '',
        sponsorshipInterest: '',
        commitToFillTable: '',
        commitToDonate: ''
    });

    const handleApply = async () => {
        if (!userInfo) return;

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            // Pass form data along with userInfo if needed, or just userInfo and assume API handles it?
            // The current submitTableCaptainRequest function signature in Guests.js uses "userInfo".
            // I'll need to update the API call to pass this new data.
            // Assuming I can pass an object merging userInfo and answers.
            await submitTableCaptainRequest({ ...userInfo, applicationResponses: applicationForm });
            setSubmitStatus('success');
        } catch (error) {
            console.error(error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRequestAdditionalTable = async () => {
        if (!userInfo) return;
        setIsRequestingTable(true);
        try {
            await requestAdditionalTable(userInfo.userId, userInfo.userDetails);
            addNotification('Table Request', 'success', 'Request submitted successfully!', 'system');
            // Refresh logic if needed
        } catch (err) {
            if (err.status === 409) {
                addNotification('Table Request', 'error', `Additional table request already submitted. Please wait for approval.`);
            } else if (err.status === 400) {
                addNotification('Table Request', 'error', "Failed to submit request. Please try again.");
            }
            else {
                addNotification('Table Request', 'error', "Failed to submit request. Please try again.");
            }
        } finally {
            setIsRequestingTable(false);
        }
    };

    // Input state moved up
    const [inputs, setInputs] = useState([{ id: Date.now(), name: '', email: '', tableNumber: 1 }]);
    const [inputError, setInputError] = useState(null);

    // Sync existing guests to inputs when fetched (to show them in the list if needed, or just keep separate?)
    // The previous GuestForm kept them separate in the UI but initialized inputs with them.
    // Actually, GuestForm rendered existing guests as disabled inputs.
    // We should replicate that behavior. 
    // When guests data changes, we should update inputs to include existing guests + empty rows.

    // Helper to find the next available table
    const getNextAvailableTable = React.useCallback((currentGuests) => {
        // Calculate counts based on current INPUTS or GUESTS?
        // We want to balance based on committed guests + drafted guests, 
        // but typically just check where there is room.
        // Let's use 'guests' (persisted or visual state) as the base for availability?
        // Actually, we should look at 'inputs' because that represents the current drafting state.

        // Count guests per table from CURRENT inputs
        // Note: 'currentGuests' arg allows us to pass a specific list, otherwise use 'inputs' state
        const source = currentGuests || inputs;

        for (let t = 1; t <= tableCount; t++) {
            // Count how many people are sitting at table t
            const count = source.filter(i => Number(i.tableNumber) === t && (i.name || i.isExisting)).length;
            if (count < 10) {
                return t;
            }
        }
        return 1; // Default to table 1 if all full (validation will catch overflow later)
    }, [inputs, tableCount]);

    React.useEffect(() => {
        if (guests.length > 0) {
            const mappedGuests = guests.filter(g => g.status !== 'Declined' && g.status !== 'Revoked').map((g, index) => ({
                id: g.id || `guest-${index}`,
                name: g.name,
                email: g.email,
                tableNumber: g.tableNumber || g.table_number || 1,
                isExisting: true
            }));

            // Allow adding more if not full
            // But we need to preserve user's current draft inputs if they are typing? 
            // Standard pattern: Only reset inputs on successful save or initial load.
            // Since fetchGuests is called on mount, this is fine.
            // But fetchGuests is called after save too.
            // We'll trust the user to save before fetching happens (which it does).

            // We need to merge checks: preserve draft inputs if we are just refreshing? 
            // For simplicity, let's just reset to existing + 1 empty row on fetch.

            if (mappedGuests.length < maxGuests) {
                // Find next available table based on mappedGuests
                let nextTable = 1;
                // Simple local logic since getNextAvailableTable depends on 'inputs' which isn't updated yet here.
                for (let t = 1; t <= tableCount; t++) {
                    const count = mappedGuests.filter(i => Number(i.tableNumber) === t).length;
                    if (count < 10) {
                        nextTable = t;
                        break;
                    }
                }
                mappedGuests.push({ id: Date.now(), name: '', email: '', tableNumber: nextTable });
            } else if (mappedGuests.length === 0) {
                mappedGuests.push({ id: Date.now(), name: '', email: '', tableNumber: 1 });
            }
            setInputs(mappedGuests);
        } else {
            // If no guests, just empty row
            // But check if we already have inputs (user typing?)
            // For now, reset.
            setInputs([{ id: Date.now(), name: '', email: '', tableNumber: 1 }]);
        }
    }, [guests, maxGuests, tableCount]);

    const handleInputChange = (id, field, value) => {
        setInputs(prev => prev.map(input =>
            input.id === id ? { ...input, [field]: value } : input
        ));
        // If updating table number of an existing guest, we also need to update the 'guests' state 
        // because TableVisualizer relies on 'guests' state, not 'inputs'.
        // This keeps visualizer in sync with the form for existing guests.
        // CHECK IF IT IS AN EXISTING GUEST FIRST to avoid unnecessary state updates that trigger resets
        const isExistingGuest = guests.some(g => g.id === id);

        if (field === 'tableNumber' && isExistingGuest) {
            setGuests(prev => prev.map(g => {
                // Find visible guest that corresponds to this input
                // The input.id for existing guests is g.id
                if (g.id === id) {
                    return { ...g, tableNumber: Number(value) };
                }
                return g;
            }));
        }
    };

    const addInputRow = () => {
        if (inputs.length >= maxGuests) return;
        const nextTable = getNextAvailableTable();
        setInputs(prev => [...prev, { id: Date.now() + Math.random(), name: '', email: '', tableNumber: nextTable }]);
    };

    const removeInputRow = (id) => {
        if (inputs.length === 1 && !inputs[0].isExisting) {
            setInputs([{ ...inputs[0], name: '', email: '', tableNumber: 1 }]);
            return;
        }
        setInputs(prev => prev.filter(input => input.id !== id));
    };

    const [revokeModalState, setRevokeModalState] = useState({ show: false, guest: null });
    const [isRevoking, setIsRevoking] = useState(false);
    const [remindModalState, setRemindModalState] = useState({ show: false, guest: null });
    const [isReminding, setIsReminding] = useState(false);

    // Updated handleGuestDrop to sync with inputs
    const handleGuestDrop = (guestId, targetTable) => {
        setGuests(prev => {
            const newGuests = prev.map(g => g.id === guestId ? { ...g, tableNumber: targetTable } : g);

            // Also update inputs to match this visual change
            setInputs(currInputs => currInputs.map(i => i.id === guestId ? { ...i, tableNumber: targetTable } : i));


            return newGuests;
        });
    };

    const handleConfirmRevoke = async () => {
        if (!revokeModalState.guest) return;

        setIsRevoking(true);
        try {
            await revokeGuest(revokeModalState.guest.id, userInfo.userId);
            // Updating local state to reflect revoked status immediately
            const revokedGuestId = revokeModalState.guest.id;

            setGuests(prev => prev.map(g =>
                g.id === revokedGuestId ? { ...g, status: 'Revoked' } : g
            ));

            // Also update inputs to disable the row
            setInputs(prev => prev.map(i =>
                i.id === revokedGuestId ? { ...i, status: 'Revoked' } : i
            ));

            addNotification('Guest Revoked', 'success', `Invitation for ${revokeModalState.guest.name} has been revoked.`, 'system');
            setRevokeModalState({ show: false, guest: null });
        } catch (error) {
            console.error(error);
            addNotification('Revoke Failed', 'error', 'Failed to revoke guest. Please try again.', 'system');
        } finally {
            setIsRevoking(false);
        }
    };

    const handleConfirmRemind = async () => {
        if (!remindModalState.guest) return;
        setIsReminding(true);
        try {
            await remindGuest(remindModalState.guest.id);
            addNotification('Reminder Sent', 'success', `A reminder email has been sent to ${remindModalState.guest.name}.`, 'system');
            setRemindModalState({ show: false, guest: null });
        } catch (error) {
            console.error(error);
            addNotification('Reminder Failed', 'error', `Failed to send reminder to ${remindModalState.guest.name}. Please try again.`, 'system');
        } finally {
            setIsReminding(false);
        }
    };

    // Helper to check for validation errors
    const getTableValidationErrors = () => {
        const errors = [];
        // Determine total guests per table based on INPUTS (which serves as the source of truth for current state)
        // because inputs includes both existing guests (with updated table numbers) and new drafts.
        // Wait, guests in 'inputs' that are marked 'isExisting' line up with 'guests'.
        // New guests are just objects.

        // Let's count from inputs, filtering out empty rows.
        const validInputs = inputs.filter(i => (i.isExisting && i.name) || (!i.isExisting && i.name.trim()));

        for (let i = 1; i <= tableCount; i++) {
            const count = validInputs.filter(inp => Number(inp.tableNumber) === i).length;
            if (count > 10) {
                errors.push(`Table ${i} has ${count} guests (max 10)`);
            }
        }
        return errors;
    };

    const handleUnifiedSave = async () => {
        const validationErrors = getTableValidationErrors();
        if (validationErrors.length > 0) {
            addNotification('Save Failed', 'error', 'Please ensure all tables have 10 or fewer guests.', 'system');
            return;
        }

        // 1. Identify new guests to invite
        const guestsToAdd = inputs
            .filter(i => !i.isExisting && i.name.trim())
            .map(i => ({
                name: i.name.trim(),
                email: i.email.trim(),
                tableNumber: Number(i.tableNumber) || 1
            }));

        // 2. Identify table updates for existing guests
        // We look at 'guests' state since that tracks the visual drops and form updates for existing users
        const updates = guests.filter(g => {
            const original = originalGuests.find(og => og.id === g.id);
            if (!original) return false;
            return (g.tableNumber || 1) !== (original.tableNumber || 1);
        }).map(g => ({
            guestId: g.id,
            tableNumber: Math.floor(Number(g.tableNumber || 1)) // Ensure integer
        }));

        if (guestsToAdd.length === 0 && updates.length === 0) {
            addNotification('Info', 'success', 'No changes to save.', 'system');
            return;
        }

        setIsSaving(true);
        setInputError(null);

        try {
            // Perform Validations for new emails
            if (guestsToAdd.length > 0) {
                const emails = new Set();
                // Add existing emails (excluding Declined and Revoked)
                guests.forEach(g => {
                    if (g.email && g.status !== 'Declined' && g.status !== 'Revoked') {
                        emails.add(g.email.toLowerCase())
                    }
                });

                for (const guest of guestsToAdd) {
                    if (!guest.email) throw new Error("All new guests must have an email.");
                    if (emails.has(guest.email.toLowerCase())) throw new Error(`Duplicate email: ${guest.email}`);
                    emails.add(guest.email.toLowerCase());
                }
            }

            // Execute Updates
            const promises = [];

            // Invites
            if (guestsToAdd.length > 0) {
                const invitePromise = Promise.all(guestsToAdd.map(async (guest) => {
                    try {
                        await inviteGuest(userInfo.userId, guest.name, guest.email, userInfo.userDetails, guest.tableNumber);
                        addNotification(guest.name, 'success', `Email on its way to ${guest.name}`);
                    } catch (err) {
                        console.error(err);
                        let msg = `Failed to invite ${guest.name}.`;
                        if (err.status === 409) {
                            msg = `${guest.name} has already been invited by you or another table captain.`;
                        } else if (err.status === 400) {
                            msg = `Failed to invite ${guest.name}. Please check the email address.`;
                        } else if (err.status === 500) {
                            msg = `Failed to send email to ${guest.name}. Please try again later.`;
                        }
                        addNotification(guest.name, 'error', msg);
                    }
                }));
                promises.push(invitePromise);
            }

            // Table Moves
            if (updates.length > 0) {
                promises.push(updateGuestTables(updates).then(() => {
                    addNotification('System', 'success', 'Table arrangements updated.', 'system');
                }).catch(err => {
                    addNotification('System', 'error', 'Failed to update table arrangements.', 'system');
                }));
            }

            await Promise.all(promises);

            // Refresh
            await fetchGuests();

        } catch (err) {
            console.error(err);
            setInputError(err.message || "An error occurred while saving.");
        } finally {
            setIsSaving(false);
        }
    };

    const activeGuestsCount = guests.filter(g => g.status !== 'Declined').length;
    // Show request button if we have filled all current tables (activeGuestsCount >= maxGuests)
    // and we don't have a pending request (activeGuestsCount could be higher if we just added one before approval?)
    // Simplest check: if we hit the limit of current tables.
    const showRequestButton = activeGuestsCount >= maxGuests;

    return (
        <div className="guests-container">
            <div id="deadline-timer" className="deadline-timer">
                <span style={{ fontSize: '1.2rem' }}>⏳</span>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#e4e4e7' }}>
                        Guest Update Deadline
                    </span>
                    <span style={{ fontSize: '0.95rem', fontWeight: '600', color: '#fff', fontFamily: 'monospace' }}>
                        {timeLeft || 'Loading...'}
                    </span>
                </div>
            </div>

            <Navigation />
            <div className="guests-card">
                {isTableCaptain ? (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                            <h2 style={{ margin: 0 }} id="my-table-header">My Table</h2>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    onClick={() => setShowTutorial(true)}
                                    style={{
                                        background: 'rgba(241, 89, 42, 0.2)',
                                        border: '1px solid var(--primary-color)',
                                        color: 'white',
                                        padding: '0.5rem 1rem',
                                        borderRadius: '0.5rem',
                                        cursor: 'pointer',
                                        fontSize: '0.875rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px'
                                    }}
                                >
                                    <span>❓</span> Tutorial
                                </button>
                                <button
                                    id="refresh-data-btn"
                                    onClick={fetchGuests}
                                    disabled={isGuestsLoading}
                                    style={{
                                        background: 'transparent',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        color: 'var(--text-color)',
                                        padding: '0.5rem 1rem',
                                        borderRadius: '0.5rem',
                                        cursor: isGuestsLoading ? 'default' : 'pointer',
                                        fontSize: '0.875rem',
                                        opacity: isGuestsLoading ? 0.5 : 1
                                    }}
                                >
                                    {isGuestsLoading ? 'Refeshing...' : 'Refresh Data'}
                                </button>
                            </div>
                        </div>
                        {isGuestsLoading ? (
                            <div style={{ padding: '4rem', textAlign: 'center', color: '#94a3b8' }}>
                                <div className="loading-skeleton skeleton-text" style={{ width: '200px', height: '2rem', margin: '0 0 1rem' }} />
                                <p>Loading your guests...</p>
                            </div>
                        ) : (
                            <>
                                <div className="table-management-container" style={{ flexDirection: 'column' }}>
                                    <div className="guest-form-section" id="guest-management-section">
                                        <h3>Manage Guests</h3>
                                        <p style={{ marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                                            You have {tableCount} table(s). Max guests: {maxGuests}.
                                        </p>
                                        {/* Validation Errors Overlay */}
                                        {getTableValidationErrors().length > 0 && (
                                            <div style={{ marginBottom: '1rem', padding: '1rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '0.5rem' }}>
                                                <span style={{ color: '#ef4444', fontSize: '0.9rem' }}>
                                                    Attention: {getTableValidationErrors()[0]}
                                                </span>
                                            </div>
                                        )}

                                        <div id="add-guest-form">
                                            <GuestForm
                                                inputs={inputs}
                                                handleInputChange={handleInputChange}
                                                addInputRow={addInputRow}
                                                removeInputRow={removeInputRow}
                                                isAdding={isSaving}
                                                error={inputError}
                                                maxLimit={maxGuests}
                                                currentCount={inputs.filter(i => i.name).length}
                                                isPastDeadline={isPastDeadline}
                                            />
                                        </div>

                                        <div style={{ marginTop: '2rem' }}>
                                            <button
                                                id="save-changes-btn"
                                                onClick={handleUnifiedSave}
                                                disabled={isSaving || getTableValidationErrors().length > 0 || isPastDeadline}
                                                className="submit-button"
                                                style={{
                                                    opacity: (isSaving || getTableValidationErrors().length > 0 || isPastDeadline) ? 0.7 : 1,
                                                    cursor: (isSaving || getTableValidationErrors().length > 0 || isPastDeadline) ? 'not-allowed' : 'pointer',
                                                    background: getTableValidationErrors().length > 0 ? '#ef4444' : undefined
                                                }}
                                            >
                                                {isSaving ? 'Saving Changes...' : 'Save Changes'}
                                            </button>
                                        </div>

                                        {showRequestButton && (
                                            <div style={{ marginTop: '2rem', padding: '1rem', background: 'rgba(255,255,255,0.05)', borderRadius: '0.5rem' }}>
                                                <p>You have filled your current tables. Need more space?</p>
                                                <button
                                                    onClick={handleRequestAdditionalTable}
                                                    disabled={isRequestingTable}
                                                    className="submit-button"
                                                    style={{
                                                        marginTop: '0.5rem',
                                                        background: 'transparent',
                                                        border: '1px solid rgba(255,255,255,0.2)',
                                                        opacity: isRequestingTable ? 0.6 : 1,
                                                        cursor: isRequestingTable ? 'not-allowed' : 'pointer'
                                                    }}
                                                >
                                                    {isRequestingTable ? 'Requesting...' : 'Request Additional Table'}
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {selectedGuestForMove && (
                                        <div style={{ textAlign: 'center', marginBottom: '1rem', padding: '0.75rem', background: 'rgba(14, 165, 233, 0.15)', color: '#bae6fd', border: '1px solid rgba(14, 165, 233, 0.3)', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                            <span>👆 Tap the table you want the guest to be at.</span>
                                            <button onClick={() => setSelectedGuestForMove(null)} style={{ background: 'transparent', border: 'none', color: '#7dd3fc', cursor: 'pointer', textDecoration: 'underline' }}>Cancel</button>
                                        </div>
                                    )}
                                    <div className="table-visualization-section" id="table-visualization-area" style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                                        gap: '2rem',
                                        justifyItems: 'center',
                                        width: '100%',
                                        perspective: '1000px'
                                    }}>
                                        {/* Dynamic Scaling Style */}
                                        <style dangerouslySetInnerHTML={{
                                            __html: `
                                            @media (max-width: 1400px) {
                                                .table-visualizer { transform: scale(0.9); transform-origin: top center; margin-bottom: -10px; }
                                            }
                                            @media (max-width: 1100px) {
                                                .table-visualizer { transform: scale(0.8); transform-origin: top center; margin-bottom: -30px; }
                                            }
                                            @media (max-width: 768px) {
                                                .table-visualization-section { grid-template-columns: 1fr !important; gap: 2rem !important; }
                                                .table-visualizer-wrapper { width: 100%; display: flex; justify-content: center; }
                                                .table-visualizer { transform: scale(0.8); transform-origin: top center; margin-bottom: -30px; }
                                            }
                                            @media (max-width: 480px) {
                                                .table-visualizer { transform: scale(0.65); transform-origin: top center; margin-bottom: -80px; }
                                            }
                                        `}} />

                                        {Array.from({ length: tableCount }, (_, i) => i + 1).map(num => (
                                            <div key={num} className="table-visualizer-wrapper" style={{ flex: '0 0 auto', transition: 'transform 0.3s ease' }}>
                                                <TableVisualizer
                                                    guests={guests.filter(g => g.status !== 'Declined' && g.status !== 'Revoked' && (g.tableNumber === num || (num === 1 && !g.tableNumber)))}
                                                    tableNumber={num}
                                                    label={`Table ${num}`}
                                                    onDrop={handleGuestDrop}
                                                    onRevoke={(guest) => setRevokeModalState({ show: true, guest })}
                                                    onRemind={(guest) => setRemindModalState({ show: true, guest })}
                                                    selectedGuestForMove={selectedGuestForMove}
                                                    setSelectedGuestForMove={setSelectedGuestForMove}
                                                    isPastDeadline={isPastDeadline}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {guests.filter(g => g.status === 'Declined').length > 0 && (
                                    <div className="declined-guests-section" id="declined-invitations-section" style={{ marginTop: '3rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '2rem' }}>
                                        <h3 style={{ color: '#ef4444', marginBottom: '1rem' }}>Declined Invitations</h3>
                                        <div className="guests-list">
                                            {guests.filter(g => g.status === 'Declined').map(guest => (
                                                <div key={guest.id} className="guest-item" style={{ borderColor: 'rgba(239, 68, 68, 0.2)' }}>
                                                    <div className="guest-info">
                                                        <span className="guest-name">{guest.name}</span>
                                                        <span style={{ color: '#94a3b8', fontSize: '0.9rem', marginLeft: '1rem' }}>{guest.email}</span>
                                                    </div>
                                                    <span className="guest-status" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
                                                        Declined
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                ) : (
                    <div className="guests-landing" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', padding: '2rem 1rem' }}>
                        <div style={{ textAlign: 'center', marginBottom: '3rem', maxWidth: '800px' }}>
                            <h2 style={{ fontSize: '2.5rem', marginBottom: '1.5rem', fontWeight: '700' }}>Welcome to the Team!</h2>
                            <p style={{ margin: '0 auto', fontSize: '1.1rem', lineHeight: '1.7', color: '#e4e4e7' }}>
                                Hello! Thank you for your willingness to be a table captain for the Raising Girls luncheon on April 29th, 2026.
                                Table Captains serve as ambassadors of Raising Girls, sharing their enthusiasm for our mission with their guests.
                                We look forward to a wonderful event together. Please fill out this form so we can confirm your commitment for the event.
                            </p>
                        </div>

                        {!approvalDataLoaded ? (
                            <div style={{ width: '100%', maxWidth: '700px', display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                                <div className="loading-skeleton skeleton-text" style={{ width: '100%', height: '400px', borderRadius: '1.5rem', background: 'rgba(255,255,255,0.1)' }} />
                                <div className="loading-skeleton skeleton-text" style={{ width: '100%', height: '3.5rem', borderRadius: '0.75rem', background: 'rgba(255,255,255,0.1)' }} />
                            </div>
                        ) : (
                            <>
                                {!approvalExists && submitStatus !== 'success' && (
                                    <div style={{
                                        width: '100%',
                                        maxWidth: '700px',
                                        background: 'rgba(24, 24, 27, 0.4)',
                                        backdropFilter: 'blur(12px)',
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                        borderRadius: '1.5rem',
                                        padding: '2.5rem',
                                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                                    }}>
                                        <form className="captain-application-form">
                                            <div className="form-group" style={{ marginBottom: '2.5rem' }}>
                                                <label style={{ display: 'block', fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem' }}>
                                                    1. Did you attend the Raising Girls luncheon last year?
                                                </label>
                                                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                                                    {['Yes', 'No'].map(opt => (
                                                        <button
                                                            key={opt}
                                                            type="button"
                                                            onClick={() => setApplicationForm({ ...applicationForm, attendedLastYear: opt })}
                                                            style={{
                                                                flex: 1,
                                                                padding: '0.75rem 1.5rem',
                                                                borderRadius: '0.5rem',
                                                                border: `1px solid ${applicationForm.attendedLastYear === opt ? 'var(--primary-color)' : 'rgba(255,255,255,0.1)'}`,
                                                                background: applicationForm.attendedLastYear === opt ? 'var(--primary-color)' : 'rgba(255,255,255,0.05)',
                                                                color: 'white',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s',
                                                                fontWeight: '500'
                                                            }}
                                                        >
                                                            {opt}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="form-group" style={{ marginBottom: '2.5rem' }}>
                                                <label style={{ display: 'block', fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem' }}>
                                                    2. Table Captain Sponsorship (optional)
                                                </label>
                                                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1rem 1.25rem', borderRadius: '0.75rem', marginBottom: '1.5rem' }}>
                                                    <p style={{ fontSize: '0.95rem', color: '#d4d4d8', marginBottom: '0.5rem', lineHeight: '1.6' }}>
                                                        <b>Through a Table Captain Sponsorship of $400, you can underwrite the meal costs at your table.</b> If you choose to sponsor your table at $400, you will be recognized in the program and signage at your table.
                                                    </p>
                                                    <p style={{ fontSize: '0.95rem', color: '#a1a1aa', fontStyle: 'italic', margin: 0 }}>
                                                        Table Sponsorship is optional and not a requirement of being a Table Captain.
                                                    </p>
                                                </div>

                                                <p style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '0.75rem', color: '#e4e4e7' }}>Will you be able to sponsor a table?</p>

                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                    <div style={{ display: 'flex', gap: '1rem' }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => setApplicationForm({ ...applicationForm, sponsorshipInterest: 'Yes' })}
                                                            style={{
                                                                flex: 1,
                                                                padding: '0.75rem',
                                                                borderRadius: '0.5rem',
                                                                border: `1px solid ${applicationForm.sponsorshipInterest === 'Yes' ? 'var(--primary-color)' : 'rgba(255,255,255,0.1)'}`,
                                                                background: applicationForm.sponsorshipInterest === 'Yes' ? 'var(--primary-color)' : 'rgba(255,255,255,0.05)',
                                                                color: 'white',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s'
                                                            }}
                                                        >
                                                            Yes
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setApplicationForm({ ...applicationForm, sponsorshipInterest: 'No' })}
                                                            style={{
                                                                flex: 1,
                                                                padding: '0.75rem',
                                                                borderRadius: '0.5rem',
                                                                border: `1px solid ${applicationForm.sponsorshipInterest === 'No' ? 'var(--primary-color)' : 'rgba(255,255,255,0.1)'}`,
                                                                background: applicationForm.sponsorshipInterest === 'No' ? 'var(--primary-color)' : 'rgba(255,255,255,0.05)',
                                                                color: 'white',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s'
                                                            }}
                                                        >
                                                            No
                                                        </button>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setApplicationForm({ ...applicationForm, sponsorshipInterest: 'Yes with Co-Captains' })}
                                                        style={{
                                                            width: '100%',
                                                            padding: '0.75rem',
                                                            borderRadius: '0.5rem',
                                                            border: `1px solid ${applicationForm.sponsorshipInterest === 'Yes with Co-Captains' ? 'var(--primary-color)' : 'rgba(255,255,255,0.1)'}`,
                                                            background: applicationForm.sponsorshipInterest === 'Yes with Co-Captains' ? 'var(--primary-color)' : 'rgba(255,255,255,0.05)',
                                                            color: 'white',
                                                            cursor: 'pointer',
                                                            textAlign: 'center',
                                                            transition: 'all 0.2s'
                                                        }}
                                                    >
                                                        Yes and I have co-captains <span style={{ display: 'block', fontSize: '0.85rem', opacity: 0.8, marginTop: '0.2rem' }}>(e.g. I am splitting the cost of the table with another person/people)</span>
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="form-group" style={{ marginBottom: '2.5rem' }}>
                                                <label style={{ display: 'block', fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem' }}>
                                                    3. Table Commitment
                                                </label>
                                                <p style={{ fontSize: '0.95rem', color: '#d4d4d8', marginBottom: '1rem', lineHeight: '1.6' }}>
                                                    As a Table Captain you commit to filling at least one table of ten at the event (you + 9 guests).
                                                </p>

                                                <p style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '0.75rem', color: '#e4e4e7' }}>Will you be able to commit to this?</p>

                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                    <div style={{ display: 'flex', gap: '1rem' }}>
                                                        {['Yes', 'No'].map(opt => (
                                                            <button
                                                                key={opt}
                                                                type="button"
                                                                onClick={() => setApplicationForm({ ...applicationForm, commitToFillTable: opt })}
                                                                style={{
                                                                    flex: 1,
                                                                    padding: '0.75rem',
                                                                    borderRadius: '0.5rem',
                                                                    border: `1px solid ${applicationForm.commitToFillTable === opt ? 'var(--primary-color)' : 'rgba(255,255,255,0.1)'}`,
                                                                    background: applicationForm.commitToFillTable === opt ? 'var(--primary-color)' : 'rgba(255,255,255,0.05)',
                                                                    color: 'white',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s'
                                                                }}
                                                            >
                                                                {opt}
                                                            </button>
                                                        ))}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setApplicationForm({ ...applicationForm, commitToFillTable: 'Sponsor but RG fill' })}
                                                        style={{
                                                            width: '100%',
                                                            padding: '0.75rem',
                                                            borderRadius: '0.5rem',
                                                            border: `1px solid ${applicationForm.commitToFillTable === 'Sponsor but RG fill' ? 'var(--primary-color)' : 'rgba(255,255,255,0.1)'}`,
                                                            background: applicationForm.commitToFillTable === 'Sponsor but RG fill' ? 'var(--primary-color)' : 'rgba(255,255,255,0.05)',
                                                            color: 'white',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s'
                                                        }}
                                                    >
                                                        I would like to sponsor a table, but let Raising Girls fill it with guests
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="form-group">
                                                <label style={{ display: 'block', fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem' }}>
                                                    4. Donation Commitment
                                                </label>
                                                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1rem 1.25rem', borderRadius: '0.75rem', marginBottom: '1.5rem' }}>
                                                    <p style={{ fontSize: '0.95rem', color: '#d4d4d8', marginBottom: '0.75rem', lineHeight: '1.6' }}>
                                                        During the event, attendees will be asked to invest in the critical work of Raising Girls. We are asking your guests to consider making a gift of $100 or more.
                                                    </p>
                                                    <p style={{ fontSize: '0.95rem', color: '#d4d4d8', marginBottom: '0', lineHeight: '1.6' }}>
                                                        As a Table Captain, we ask you to lead the way and make a generous donation so that you can inspire others to give too! We also ask that you <b>distribute and collect envelopes of donations at your table on the day of the event.</b>
                                                    </p>
                                                </div>

                                                <p style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '0.75rem', color: '#e4e4e7' }}>Will you be able to commit to this?</p>
                                                <div style={{ display: 'flex', gap: '1rem' }}>
                                                    {['Yes', 'No'].map(opt => (
                                                        <button
                                                            key={opt}
                                                            type="button"
                                                            onClick={() => setApplicationForm({ ...applicationForm, commitToDonate: opt })}
                                                            style={{
                                                                flex: 1,
                                                                padding: '0.75rem',
                                                                borderRadius: '0.5rem',
                                                                border: `1px solid ${applicationForm.commitToDonate === opt ? 'var(--primary-color)' : 'rgba(255,255,255,0.1)'}`,
                                                                background: applicationForm.commitToDonate === opt ? 'var(--primary-color)' : 'rgba(255,255,255,0.05)',
                                                                color: 'white',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s',
                                                                fontWeight: '500'
                                                            }}
                                                        >
                                                            {opt}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                )}

                                {
                                    submitStatus === 'success' || approvalExists ? (
                                        <div className="success-message" style={{ marginTop: '2rem', color: '#4ade80', background: 'rgba(74, 222, 128, 0.1)', padding: '1.5rem', borderRadius: '1rem', border: '1px solid rgba(74, 222, 128, 0.2)', width: '100%', maxWidth: '700px', textAlign: 'center' }}>
                                            <p style={{ fontSize: '1.1rem', margin: 0, lineHeight: '1.6' }}>
                                                {approvalExists
                                                    ? "An approval request has already been submitted. Please wait for an admin to approve your request."
                                                    : "Request submitted successfully! We will review it shortly."}
                                            </p>
                                        </div>
                                    ) : (
                                        <div style={{ marginTop: '2.5rem', width: '100%', maxWidth: '700px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <button
                                                className="submit-button"
                                                type="button"
                                                onClick={handleApply}
                                                disabled={isSubmitting || !applicationForm.attendedLastYear || !applicationForm.sponsorshipInterest || !applicationForm.commitToFillTable || !applicationForm.commitToDonate}
                                                style={{
                                                    width: '100%',
                                                    padding: '1rem',
                                                    fontSize: '1.1rem',
                                                    opacity: (!applicationForm.attendedLastYear || !applicationForm.sponsorshipInterest || !applicationForm.commitToFillTable || !applicationForm.commitToDonate) ? 0.5 : 1,
                                                    boxShadow: '0 4px 14px 0 rgba(241, 89, 42, 0.39)',
                                                    borderRadius: '0.75rem'
                                                }}
                                            >
                                                {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
                                            </button>
                                            {submitStatus === 'error' && (
                                                <p style={{ color: '#f87171', marginTop: '1rem' }}>
                                                    Something went wrong. Please try again.
                                                </p>
                                            )}
                                        </div>
                                    )
                                }
                            </>
                        )}
                    </div >
                )}
            </div >
            {/* Notifications Rendered Here to Persist */}
            < div className="notification-container" >
                {
                    notifications.map(n => (
                        <div key={n.id} className="envelope-notification" style={n.type === 'error' ? { background: 'rgba(239, 68, 68, 0.9)' } : {}}>
                            <button
                                onClick={() => removeNotification(n.id)}
                                style={{
                                    position: 'absolute',
                                    top: '5px',
                                    right: '5px',
                                    background: 'transparent',
                                    border: 'none',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    padding: '5px',
                                    lineHeight: 1
                                }}
                                title="Dismiss"
                            >
                                ×
                            </button>
                            <div className="envelope-icon">{n.type === 'error' ? '⚠️' : '📨'}</div>
                            <div>
                                <div style={{ fontWeight: '600' }}>
                                    {n.type === 'error' ? 'Error' : 'Success'}
                                </div>
                                <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                                    <span>{n.message}</span>
                                </div>
                            </div>
                        </div>
                    ))
                }
            </div >

            {/* Revoke Confirmation Modal */
                revokeModalState.show && revokeModalState.guest && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        backdropFilter: 'blur(5px)'
                    }}>
                        <div style={{
                            background: '#1f2937',
                            padding: '2rem',
                            borderRadius: '1rem',
                            width: '90%',
                            maxWidth: '500px',
                            border: '1px solid rgba(255,255,255,0.1)',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                        }}>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#f3f4f6', marginBottom: '1rem' }}>Revoke Invitation?</h3>
                            <p style={{ color: '#d1d5db', marginBottom: '1.5rem', lineHeight: '1.5' }}>
                                Are you sure you want to revoke the invitation for <strong style={{ color: 'white' }}>{revokeModalState.guest.name}</strong>?
                                They will no longer be able to access the event details.
                            </p>
                            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                                <button
                                    onClick={() => setRevokeModalState({ show: false, guest: null })}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        borderRadius: '0.5rem',
                                        background: 'transparent',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        color: 'white',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmRevoke}
                                    disabled={isRevoking}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        borderRadius: '0.5rem',
                                        background: isRevoking ? '#991b1b' : '#ef4444',
                                        border: 'none',
                                        color: 'white',
                                        fontWeight: 600,
                                        cursor: isRevoking ? 'not-allowed' : 'pointer',
                                        opacity: isRevoking ? 0.7 : 1
                                    }}
                                >
                                    {isRevoking ? 'Revoking...' : 'Yes, Revoke'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Remind Confirmation Modal */
                remindModalState.show && remindModalState.guest && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        backdropFilter: 'blur(5px)'
                    }}>
                        <div style={{
                            background: '#1f2937',
                            padding: '2rem',
                            borderRadius: '1rem',
                            width: '90%',
                            maxWidth: '500px',
                            border: '1px solid rgba(255,255,255,0.1)',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                        }}>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#f3f4f6', marginBottom: '1rem' }}>Remind Guest?</h3>
                            <p style={{ color: '#d1d5db', marginBottom: '1.5rem', lineHeight: '1.5' }}>
                                Are you sure you want to send a reminder email to <strong style={{ color: 'white' }}>{remindModalState.guest.name}</strong>?
                            </p>
                            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                                <button
                                    onClick={() => setRemindModalState({ show: false, guest: null })}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        borderRadius: '0.5rem',
                                        background: 'transparent',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        color: 'white',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmRemind}
                                    disabled={isReminding}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        borderRadius: '0.5rem',
                                        background: isReminding ? '#854d0e' : '#eab308',
                                        border: 'none',
                                        color: 'white',
                                        fontWeight: 600,
                                        cursor: isReminding ? 'not-allowed' : 'pointer',
                                        opacity: isReminding ? 0.7 : 1
                                    }}
                                >
                                    {isReminding ? 'Sending...' : 'Yes, Remind'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
            {/* Tour Guide */}
            <TourGuide
                steps={tourSteps}
                isOpen={showTutorial}
                onRequestClose={() => setShowTutorial(false)}
            />
        </div >
    );
}

export default Guests;

const GuestForm = ({ inputs, handleInputChange, addInputRow, removeInputRow, isAdding, error, maxLimit, currentCount, isPastDeadline }) => {
    const existingInputs = inputs.filter(i => i.isExisting);
    const newInputs = inputs.filter(i => !i.isExisting);
    const [isExistingExpanded, setIsExistingExpanded] = React.useState(false);

    const renderInputRow = (input) => (
        <div key={input.id} className="form-group guest-input-row">
            <div className="guest-input-col">
                <input
                    type="text"
                    value={input.name}
                    onChange={(e) => handleInputChange(input.id, 'name', e.target.value)}
                    placeholder={`Guest Name`}
                    disabled={isAdding || input.isExisting || isPastDeadline}
                    style={input.isExisting || isPastDeadline ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
                />
            </div>
            <div className="guest-input-col">
                <input
                    type="email"
                    value={input.email}
                    onChange={(e) => handleInputChange(input.id, 'email', e.target.value)}
                    placeholder={`Guest Email`}
                    disabled={isAdding || input.isExisting || isPastDeadline}
                    style={input.isExisting || isPastDeadline ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
                    title={input.email}
                />
            </div>

            {maxLimit > 10 && (
                <div style={{ width: '100px' }}>
                    <select
                        value={input.tableNumber || 1}
                        onChange={(e) => handleInputChange(input.id, 'tableNumber', e.target.value)}
                        disabled={isAdding || isPastDeadline}
                        style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '0.5rem',
                            color: 'white',
                            cursor: 'pointer',
                            WebkitAppearance: 'none',
                            MozAppearance: 'none',
                            appearance: 'none',
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='white' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 10px center',
                            paddingRight: '30px'
                        }}
                    >
                        {Array.from({ length: Math.ceil(maxLimit / 10) }, (_, i) => i + 1).map(num => (
                            <option key={num} value={num} style={{ color: 'black' }}>Table {num}</option>
                        ))}
                    </select>
                </div>
            )}

            {inputs.length > 1 && !input.isExisting && !isPastDeadline && (
                <button
                    type="button"
                    onClick={() => removeInputRow(input.id)}
                    className="remove-btn"
                    style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', border: 'none', borderRadius: '4px', cursor: 'pointer', padding: '0 10px', height: '42px' }}
                    title="Remove row"
                >
                    ✕
                </button>
            )}
        </div>
    );

    return (

        <div className="guest-add-form">
            <div className="guest-form-layout" style={{
                display: 'flex',
                gap: '2rem',
                flexWrap: 'wrap',
                alignItems: 'flex-start'
            }}>
                {/* Left Column: New Guest Form */}
                <div className="guest-form-left" style={{ flex: '1 1 400px', minWidth: '300px' }}>
                    <h4 style={{ marginBottom: '1rem', color: '#a1a1aa' }}>Add New Guests</h4>
                    <div className="guest-inputs-list">
                        {/* Render new inputs */}
                        {newInputs.map(renderInputRow)}
                    </div>

                    <div style={{ display: 'flex', gap: '10px', marginTop: '1rem' }}>
                        <button
                            type="button"
                            onClick={addInputRow}
                            className="add-row-btn"
                            disabled={isAdding || (inputs.length >= maxLimit) || isPastDeadline}
                            style={{
                                flex: 1,
                                padding: '0.75rem',
                                background: 'rgba(255, 255, 255, 0.1)',
                                border: 'none',
                                borderRadius: '0.5rem',
                                color: 'white',
                                cursor: (isAdding || (inputs.length >= maxLimit) || isPastDeadline) ? 'not-allowed' : 'pointer',
                                opacity: (isAdding || (inputs.length >= maxLimit) || isPastDeadline) ? 0.5 : 1
                            }}
                        >
                            + Add Another
                        </button>
                    </div>

                    {error && <p className="error-text" style={{ marginTop: '1rem' }}>{error}</p>}
                </div>

                {/* Right Column: Existing/Invited Guests */}
                {existingInputs.length > 0 && (
                    <div className="guest-form-right" style={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <h4 style={{ marginBottom: '1rem', color: '#a1a1aa' }}>Invited Guest Details</h4>
                        <div style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.5rem', overflow: 'hidden' }}>
                            <button
                                type="button"
                                onClick={() => setIsExistingExpanded(!isExistingExpanded)}
                                style={{
                                    width: '100%',
                                    padding: '1rem',
                                    background: 'rgba(255,255,255,0.05)',
                                    border: 'none',
                                    color: 'white',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                            >
                                <span style={{ fontWeight: 500 }}>View Guest Details ({existingInputs.length})</span>
                                <span>{isExistingExpanded ? '▲' : '▼'}</span>
                            </button>
                            {isExistingExpanded && (
                                <div style={{ padding: '1rem', background: 'rgba(0,0,0,0.2)', maxHeight: '400px', overflowY: 'auto' }}>
                                    {existingInputs.map(renderInputRow)}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const TableVisualizer = ({ guests, tableNumber, label, onDrop, onRevoke, onRemind, selectedGuestForMove, setSelectedGuestForMove, isPastDeadline }) => {
    // Generate 10 seats
    // Generate seats based on max of 10 or current guest count to accommodate temporary overflows
    const seatCount = Math.max(10, guests.length);
    const seats = Array.from({ length: seatCount }, (_, i) => {
        // Find guest specifically for this slot if we were persisting slots,
        // but for now we just fill slots in order.
        const guest = guests[i];

        // Calculate position around a circle
        const angle = (i * 360) / seatCount;
        const radius = 120; // Radius of the table circle
        const x = Math.cos((angle - 90) * (Math.PI / 180)) * radius;
        const y = Math.sin((angle - 90) * (Math.PI / 180)) * radius;

        return { id: i, x, y, guest };
    });

    const handleDragStart = (e, guestId) => {
        e.dataTransfer.setData("guestId", guestId);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDropInternal = (e) => {
        e.preventDefault();
        const guestId = e.dataTransfer.getData("guestId");
        if (guestId && onDrop) {
            onDrop(guestId, tableNumber);
        }
    };

    return (
        <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '350px' }}
            onDragOver={handleDragOver}
            onDrop={handleDropInternal}
        >
            <div
                className="table-visualizer"
                style={{ marginBottom: '1rem' }}
            >
                <div
                    className="table-circle"
                    style={{
                        borderColor: selectedGuestForMove ? '#0ea5e9' : (guests.length > 10 ? 'rgba(239, 68, 68, 0.5)' : ''),
                        cursor: selectedGuestForMove ? 'pointer' : 'default',
                        boxShadow: selectedGuestForMove ? '0 0 20px rgba(14, 165, 233, 0.3) inset' : 'none',
                        transition: 'all 0.3s'
                    }}
                    onClick={() => {
                        if (selectedGuestForMove && onDrop) {
                            onDrop(selectedGuestForMove, tableNumber);
                            setSelectedGuestForMove(null);
                        }
                    }}
                >
                    <div className="table-center-label">
                        {label || 'Table 1'}
                        {guests.length > 10 && (
                            <div style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                                {guests.length}/10
                            </div>
                        )}
                    </div>
                    {guests.length > 10 && (
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            fontSize: '4rem',
                            opacity: 0.2,
                            color: '#ef4444',
                            pointerEvents: 'none'
                        }}>
                            ⚠
                        </div>
                    )}
                </div>
                {seats.map((seat) => {
                    let statusColor = 'rgba(255, 255, 255, 0.05)'; // default/empty
                    let borderColor = 'rgba(255, 255, 255, 0.15)';
                    let isOccupied = !!seat.guest;

                    if (isOccupied) {
                        if (seat.guest.status === 'Accepted') {
                            statusColor = 'linear-gradient(135deg, #22c55e, #16a34a)'; // Green
                            borderColor = 'rgba(255, 255, 255, 0.4)';
                        } else if (seat.guest.status === 'Invited') {
                            statusColor = 'linear-gradient(135deg, #eab308, #ca8a04)'; // Yellow
                            borderColor = 'rgba(255, 255, 255, 0.4)';
                        } else {
                            statusColor = 'linear-gradient(135deg, #8b5cf6, #6366f1)'; // Default Purple
                        }
                    }

                    return (
                        <div
                            key={seat.id}
                            className={`table-seat ${isOccupied ? 'occupied' : 'empty'} ${selectedGuestForMove === (isOccupied ? seat.guest.id : null) ? 'pulse-border' : ''}`}
                            style={{
                                transform: `translate(${seat.x}px, ${seat.y}px)`,
                                background: statusColor,
                                borderColor: selectedGuestForMove === (isOccupied ? seat.guest.id : null) ? '#0ea5e9' : borderColor,
                                cursor: isOccupied ? 'grab' : 'pointer',
                                boxShadow: selectedGuestForMove === (isOccupied ? seat.guest.id : null) ? '0 0 15px #0ea5e9' : 'none',
                                zIndex: selectedGuestForMove === (isOccupied ? seat.guest.id : null) ? 10 : 1
                            }}
                            draggable={isOccupied && !isPastDeadline}
                            onDragStart={(e) => isOccupied && !isPastDeadline && handleDragStart(e, seat.guest.id)}
                            onClick={() => {
                                if (isPastDeadline) return;
                                if (isOccupied) {
                                    if (selectedGuestForMove === seat.guest.id) {
                                        setSelectedGuestForMove(null);
                                    } else {
                                        setSelectedGuestForMove(seat.guest.id);
                                    }
                                } else if (selectedGuestForMove && onDrop) {
                                    onDrop(selectedGuestForMove, tableNumber);
                                    setSelectedGuestForMove(null);
                                }
                            }}
                        >
                            <div className="seat-tooltip">
                                {isOccupied ? `${seat.guest.name} (${seat.guest.status})` : 'Empty Seat'}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Guest List for this Table - Now Flowing Naturally */}
            <div style={{ width: '100%', textAlign: 'left', background: 'rgba(255,255,255,0.03)', padding: '1rem', borderRadius: '1rem', border: '1px solid rgba(255,255,255,0.05)' }}>
                <h4 style={{ color: '#a1a1aa', marginBottom: '0.75rem', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '0.5rem' }}>
                    Guests ({guests.length}/10)
                </h4>
                {guests.length === 0 ? (
                    <p style={{ color: '#71717a', fontStyle: 'italic', fontSize: '0.875rem', textAlign: 'center', padding: '1rem 0' }}>Drag guests here</p>
                ) : (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {guests.map((guest) => (
                            <li
                                key={guest.id}
                                className="guest-list-item"
                                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid rgba(255,255,255,0.02)', position: 'relative' }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0 }}>
                                    <span style={{ color: '#e4e4e7', fontSize: '0.9rem', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{guest.name}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                                    <div style={{ width: '70px', display: 'flex', justifyContent: 'center' }}>
                                        <span style={{
                                            fontSize: '0.7rem',
                                            padding: '2px 8px',
                                            borderRadius: '999px',
                                            background: guest.status === 'Accepted' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(234, 179, 8, 0.1)',
                                            color: guest.status === 'Accepted' ? '#4ade80' : '#facc15',
                                            border: '1px solid',
                                            borderColor: guest.status === 'Accepted' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(234, 179, 8, 0.2)'
                                        }}>
                                            {guest.status}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '4px', width: '70px', justifyContent: 'flex-end' }}>
                                        {guest.status !== 'Accepted' && onRemind && !isPastDeadline && (
                                            <button
                                                onClick={() => onRemind(guest)}
                                                className="revoke-btn"
                                                title="Remind Guest"
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: '#facc15',
                                                    cursor: 'pointer',
                                                    fontSize: '1rem',
                                                    padding: '0 4px',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                🔔
                                            </button>
                                        )}
                                        {(guest.status === 'Invited' || guest.status === 'Accepted') && onRevoke && !isPastDeadline && (
                                            <button
                                                onClick={() => onRevoke(guest)}
                                                className="revoke-btn"
                                                title="Revoke Invitation"
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: '#ef4444',
                                                    cursor: 'pointer',
                                                    fontSize: '1rem',
                                                    padding: '0 4px',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <style jsx>{`
                                    .revoke-btn { 
                                        opacity: 1 !important; 
                                        transition: opacity 0.2s; 
                                    }
                                    @media (max-width: 768px) {
                                        .revoke-btn { 
                                            background: rgba(255,255,255,0.05) !important; 
                                            padding: 6px 10px !important; 
                                            border-radius: 4px;
                                            margin-left: 4px;
                                        }
                                    }
                                `}</style>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};
